/**
 * UploadPhotoModal - Clean, dedicated photo upload modal
 * 
 * ONLY photo upload fields:
 * - Image preview (thumbnail)
 * - Caption (optional)
 * - Visibility
 * - Cancel / Upload
 * 
 * NO visit/schedule UI. NO date fields. NO multi-day controls.
 */

import { useState, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { Camera, Upload, X, SpinnerGap, Eye, EyeSlash, Image as ImageIcon, Warning } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function UploadPhotoModal({
  open,
  onOpenChange,
  visitId,
  onUploadComplete,
}) {
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setPreview(null);
    setCaption("");
    setVisibility("internal");
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear any previous error
    setUploadError(null);

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview({
        dataUrl: e.target.result,
        file: file,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!preview?.file || !visitId) return;

    setUploading(true);
    setUploadError(null);
    
    try {
      const formData = new FormData();
      formData.append("file", preview.file);
      formData.append("caption", caption);
      formData.append("visibility", visibility);

      const response = await fetch(`${API}/photos/visit/${visitId}`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Photo uploaded");
        onUploadComplete?.(data);
        handleClose();
      } else {
        // Show the exact backend error message
        const error = await response.json();
        const errorMessage = error.detail || "Failed to upload photo";
        setUploadError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = "Failed to upload photo. Check your connection.";
      setUploadError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="upload-photo-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg uppercase">Upload Photo</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            {!preview ? (
              /* File selection area */
              <div className="border-2 border-dashed border-zinc-300 rounded-sm p-8 text-center">
                <Camera size={48} className="mx-auto mb-4 text-zinc-400" />
                <p className="text-sm text-zinc-500 mb-4">Take a photo or choose from gallery</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="photo-upload-input"
                />
                <label htmlFor="photo-upload-input">
                  <Button variant="outline" className="border-2" asChild>
                    <span>
                      <Camera size={18} className="mr-2" />
                      Select Photo
                    </span>
                  </Button>
                </label>
              </div>
            ) : (
              /* Preview and form */
              <>
                {/* Image preview thumbnail */}
                <div className="relative rounded-sm overflow-hidden border-2 border-zinc-200">
                  <img
                    src={preview.dataUrl}
                    alt="Preview"
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={() => setPreview(null)}
                    className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Caption input */}
                <div>
                  <Label>Caption (optional)</Label>
                  <Input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Add a description..."
                    className="mt-1 border-2"
                    data-testid="photo-caption-input"
                  />
                </div>

                {/* Visibility select */}
                <div>
                  <Label>Visibility</Label>
                  <Select value={visibility} onValueChange={setVisibility}>
                    <SelectTrigger className="mt-1 border-2" data-testid="photo-visibility-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">
                        <div className="flex items-center gap-2">
                          <EyeSlash size={16} />
                          Internal Only
                        </div>
                      </SelectItem>
                      <SelectItem value="customer">
                        <div className="flex items-center gap-2">
                          <Eye size={16} />
                          Visible to Customer
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">
                    {visibility === "internal"
                      ? "Only visible to your team"
                      : "Customer will see this photo"}
                  </p>
                </div>
              </>
            )}

            {/* Show upload error if any */}
            {uploadError && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-md">
                <Warning size={16} />
                <span>{uploadError}</span>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="flex-1 border-2">
            Cancel
          </Button>
          {preview && (
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="upload-photo-submit"
            >
              {uploading ? (
                <>
                  <SpinnerGap size={18} className="mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} className="mr-2" />
                  Upload
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UploadPhotoModal;
